export enum PermissionModule {
  ENQUIRY = "enquiry",
  WORK_ORDER = "work_order",
  PETTY_WORK = "petty_work",
  DASHBOARD = "dashboard",
  SITE_VISIT = "site_visit",
  AMC_VISIT = "amc_visit",
  AMC = "amc",
  EMPLOYEE_ATTENDANCE = "employee_attendance",
  EMPLOYEE = "employee",
  EMPLOYEE_SALARY_STRUCTURE = "employee_salary_structure",
  EMPLOYMENT_CATEGORY = "employment_category",
  LEAVE_APPLICATION = "leave_application",
  LEAVE_TYPE = "leave_type",
  LEAVE_POLICY = "leave_policy",
  USER = "user",
  ROLES_AND_PERMISSIONS = "roles_and_permissions",
  CLIENT = "client",
  QUOTATION = "quotation",
  CLIENT_ADVANCE = "client_advance",
  CLIENT_ADVANCE_SETTLEMENT = "client_advance_settlement",
  EMPLOYEE_ADVANCE = "employee_advance",
  EMPLOYEE_ADVANCE_SETTLEMENT = "employee_advance_settlement",
  PAYROLL = "payroll",
  MATERIAL_REQUEST = "material_request",
  INVOICE = "invoice",
  PAYMENT = "payment",
  SUPPLIER = "supplier",
  PURCHASE_ORDER = "purchase_order",
  WORK_ASSIGNMENT = "work_assignment",
  DEPARTMENT = "department",
  DESIGNATION = "designation",
  SETTINGS = "settings",
  HOLIDAY = "holiday",
  PAYMENT_EXPECTATION = "payment_expectation",
  ACTIVITY_LOG = "activity_log",
  MATERIAL = "material",
  TOOL = "tool",
  MATERIAL_IN_OUT = "material_in_out",
  TOOL_IN_OUT = "tool_in_out",
  COMPLAINT_MATERIAL = "complaint_material",
}

export enum PermissionAction {
  CREATE = "create",
  READ = "read",
  UPDATE = "update",
  DELETE = "delete",

  APPROVE = "approve",
  /** AMC DRAFT -> READY_FOR_ACTIVATION */
  READY_FOR_ACTIVATION = "ready_for_activation",
  CONFIRM = "confirm",
  CANCEL = "cancel",
  CLOSE = "close",
  TERMINATE = "terminate",
  REJECT = "reject",
  PROCESS = "process",
  SEND = "send",
  ACCEPT = "accept",
  /** Work order, petty work, site visit, AMC visit state transitions */
  SCHEDULE = "schedule",
  START = "start",
  HOLD = "hold",
  RESUME = "resume",
  COMPLETE = "complete",
  SUBMIT_BILLING = "submit_billing",
  /** Cancel the current invoice and generate a fresh one from corrected data. */
  REGENERATE_INVOICE = "regenerate_invoice",
  /** Client advance DRAFT → PROCESSED (attach the Payment). */
  RECEIVE = "receive",
  /** Client advance PROCESSED → REFUNDED (full remaining balance only). */
  REFUND = "refund",
  /** Employee advance DRAFT → PROCESSED (attach the Payment). */
  DISBURSE = "disburse",
  /** Payroll run */
  FINALIZE = "finalize",
  // Assignments
  ASSIGN = "assign",
  REASSIGN = "reassign",
  // Attendance
  CLOCK_IN = "clock_in",
  CLOCK_OUT = "clock_out",
  // Scoped read
  READ_BASIC = "read_basic",
  /** Narrower than READ — grants only the module's report view. */
  VIEW_REPORT = "view_report",
  /** Material/Tool In-Out: mark a checked-out row as returned. */
  RETURN = "return",
}

export const buildPermission = (
  module: PermissionModule,
  action: PermissionAction,
) => {
  return `${module}.${action}`;
};

export const Permissions = {
  DASHBOARD: {
    READ: buildPermission(PermissionModule.DASHBOARD, PermissionAction.READ),
  },
  ENQUIRY: {
    CREATE: buildPermission(PermissionModule.ENQUIRY, PermissionAction.CREATE),
    READ: buildPermission(PermissionModule.ENQUIRY, PermissionAction.READ),
    UPDATE: buildPermission(PermissionModule.ENQUIRY, PermissionAction.UPDATE),
    DELETE: buildPermission(PermissionModule.ENQUIRY, PermissionAction.DELETE),
  },

  SITE_VISIT: {
    CREATE: buildPermission(
      PermissionModule.SITE_VISIT,
      PermissionAction.CREATE,
    ),
    UPDATE: buildPermission(
      PermissionModule.SITE_VISIT,
      PermissionAction.UPDATE,
    ),
    READ: buildPermission(PermissionModule.SITE_VISIT, PermissionAction.READ),
    DELETE: buildPermission(
      PermissionModule.SITE_VISIT,
      PermissionAction.DELETE,
    ),
    START: buildPermission(PermissionModule.SITE_VISIT, PermissionAction.START),
    COMPLETE: buildPermission(
      PermissionModule.SITE_VISIT,
      PermissionAction.COMPLETE,
    ),
    CANCEL: buildPermission(
      PermissionModule.SITE_VISIT,
      PermissionAction.CANCEL,
    ),
  },
  WORK_ORDER: {
    READ: buildPermission(PermissionModule.WORK_ORDER, PermissionAction.READ),
    CREATE: buildPermission(
      PermissionModule.WORK_ORDER,
      PermissionAction.CREATE,
    ),
    UPDATE: buildPermission(
      PermissionModule.WORK_ORDER,
      PermissionAction.UPDATE,
    ),
    DELETE: buildPermission(
      PermissionModule.WORK_ORDER,
      PermissionAction.DELETE,
    ),
    SCHEDULE: buildPermission(
      PermissionModule.WORK_ORDER,
      PermissionAction.SCHEDULE,
    ),
    START: buildPermission(PermissionModule.WORK_ORDER, PermissionAction.START),
    HOLD: buildPermission(PermissionModule.WORK_ORDER, PermissionAction.HOLD),
    RESUME: buildPermission(
      PermissionModule.WORK_ORDER,
      PermissionAction.RESUME,
    ),
    CLOSE: buildPermission(PermissionModule.WORK_ORDER, PermissionAction.CLOSE),
    CANCEL: buildPermission(
      PermissionModule.WORK_ORDER,
      PermissionAction.CANCEL,
    ),
    APPROVE: buildPermission(
      PermissionModule.WORK_ORDER,
      PermissionAction.APPROVE,
    ),
    SUBMIT_BILLING: buildPermission(
      PermissionModule.WORK_ORDER,
      PermissionAction.SUBMIT_BILLING,
    ),
    REGENERATE_INVOICE: buildPermission(
      PermissionModule.WORK_ORDER,
      PermissionAction.REGENERATE_INVOICE,
    ),
    VIEW_REPORT: buildPermission(
      PermissionModule.WORK_ORDER,
      PermissionAction.VIEW_REPORT,
    ),
  },
  PETTY_WORK: {
    READ: buildPermission(PermissionModule.PETTY_WORK, PermissionAction.READ),
    CREATE: buildPermission(
      PermissionModule.PETTY_WORK,
      PermissionAction.CREATE,
    ),
    UPDATE: buildPermission(
      PermissionModule.PETTY_WORK,
      PermissionAction.UPDATE,
    ),
    DELETE: buildPermission(
      PermissionModule.PETTY_WORK,
      PermissionAction.DELETE,
    ),
    CANCEL: buildPermission(
      PermissionModule.PETTY_WORK,
      PermissionAction.CANCEL,
    ),
    START: buildPermission(PermissionModule.PETTY_WORK, PermissionAction.START),
    HOLD: buildPermission(PermissionModule.PETTY_WORK, PermissionAction.HOLD),
    RESUME: buildPermission(
      PermissionModule.PETTY_WORK,
      PermissionAction.RESUME,
    ),
    /** Approve for billing — IN_PROGRESS → READY_FOR_BILLING. */
    APPROVE: buildPermission(
      PermissionModule.PETTY_WORK,
      PermissionAction.APPROVE,
    ),
    /** Submit billing — READY_FOR_BILLING → COMPLETED. */
    SUBMIT_BILLING: buildPermission(
      PermissionModule.PETTY_WORK,
      PermissionAction.SUBMIT_BILLING,
    ),
    REGENERATE_INVOICE: buildPermission(
      PermissionModule.PETTY_WORK,
      PermissionAction.REGENERATE_INVOICE,
    ),
  },
  AMC_VISIT: {
    CREATE: buildPermission(
      PermissionModule.AMC_VISIT,
      PermissionAction.CREATE,
    ),
    READ: buildPermission(PermissionModule.AMC_VISIT, PermissionAction.READ),
    UPDATE: buildPermission(
      PermissionModule.AMC_VISIT,
      PermissionAction.UPDATE,
    ),
    DELETE: buildPermission(
      PermissionModule.AMC_VISIT,
      PermissionAction.DELETE,
    ),
    COMPLETE: buildPermission(
      PermissionModule.AMC_VISIT,
      PermissionAction.COMPLETE,
    ),
    CANCEL: buildPermission(
      PermissionModule.AMC_VISIT,
      PermissionAction.CANCEL,
    ),
  },

  AMC: {
    CREATE: buildPermission(PermissionModule.AMC, PermissionAction.CREATE),
    UPDATE: buildPermission(PermissionModule.AMC, PermissionAction.UPDATE),
    READ: buildPermission(PermissionModule.AMC, PermissionAction.READ),
    DELETE: buildPermission(PermissionModule.AMC, PermissionAction.DELETE),
    READY_FOR_ACTIVATION: buildPermission(
      PermissionModule.AMC,
      PermissionAction.READY_FOR_ACTIVATION,
    ),
    CANCEL: buildPermission(PermissionModule.AMC, PermissionAction.CANCEL),
    /** Approve completion — PENDING_COMPLETION_APPROVAL → COMPLETED. */
    COMPLETE: buildPermission(PermissionModule.AMC, PermissionAction.COMPLETE),
    /** Reject completion — PENDING_COMPLETION_APPROVAL → ACTIVE. */
    REJECT: buildPermission(PermissionModule.AMC, PermissionAction.REJECT),
    REGENERATE_INVOICE: buildPermission(
      PermissionModule.AMC,
      PermissionAction.REGENERATE_INVOICE,
    ),
  },

  EMPLOYEE_ATTENDANCE: {
    CREATE: buildPermission(
      PermissionModule.EMPLOYEE_ATTENDANCE,
      PermissionAction.CREATE,
    ),
    UPDATE: buildPermission(
      PermissionModule.EMPLOYEE_ATTENDANCE,
      PermissionAction.UPDATE,
    ),
    READ: buildPermission(
      PermissionModule.EMPLOYEE_ATTENDANCE,
      PermissionAction.READ,
    ),
    DELETE: buildPermission(
      PermissionModule.EMPLOYEE_ATTENDANCE,
      PermissionAction.DELETE,
    ),
    /** Close a calendar day: fill missing rows from approved leave vs absent, then lock. */
    FINALIZE: buildPermission(
      PermissionModule.EMPLOYEE_ATTENDANCE,
      PermissionAction.FINALIZE,
    ),
    CLOCK_IN: buildPermission(
      PermissionModule.EMPLOYEE_ATTENDANCE,
      PermissionAction.CLOCK_IN,
    ),
    CLOCK_OUT: buildPermission(
      PermissionModule.EMPLOYEE_ATTENDANCE,
      PermissionAction.CLOCK_OUT,
    ),
    VIEW_REPORT: buildPermission(
      PermissionModule.EMPLOYEE_ATTENDANCE,
      PermissionAction.VIEW_REPORT,
    ),
  },

  EMPLOYEE: {
    CREATE: buildPermission(PermissionModule.EMPLOYEE, PermissionAction.CREATE),
    READ: buildPermission(PermissionModule.EMPLOYEE, PermissionAction.READ),
    READ_BASIC: buildPermission(PermissionModule.EMPLOYEE, PermissionAction.READ_BASIC),
    UPDATE: buildPermission(PermissionModule.EMPLOYEE, PermissionAction.UPDATE),
    DELETE: buildPermission(PermissionModule.EMPLOYEE, PermissionAction.DELETE),
  },
  EMPLOYEE_SALARY_STRUCTURE: {
    CREATE: buildPermission(
      PermissionModule.EMPLOYEE_SALARY_STRUCTURE,
      PermissionAction.CREATE,
    ),
    READ: buildPermission(
      PermissionModule.EMPLOYEE_SALARY_STRUCTURE,
      PermissionAction.READ,
    ),
    UPDATE: buildPermission(
      PermissionModule.EMPLOYEE_SALARY_STRUCTURE,
      PermissionAction.UPDATE,
    ),
    DELETE: buildPermission(
      PermissionModule.EMPLOYEE_SALARY_STRUCTURE,
      PermissionAction.DELETE,
    ),
  },

  LEAVE_APPLICATION: {
    READ: buildPermission(
      PermissionModule.LEAVE_APPLICATION,
      PermissionAction.READ,
    ),
    CREATE: buildPermission(
      PermissionModule.LEAVE_APPLICATION,
      PermissionAction.CREATE,
    ),
    UPDATE: buildPermission(
      PermissionModule.LEAVE_APPLICATION,
      PermissionAction.UPDATE,
    ),
    DELETE: buildPermission(
      PermissionModule.LEAVE_APPLICATION,
      PermissionAction.DELETE,
    ),
    APPROVE: buildPermission(
      PermissionModule.LEAVE_APPLICATION,
      PermissionAction.APPROVE,
    ),
    REJECT: buildPermission(
      PermissionModule.LEAVE_APPLICATION,
      PermissionAction.REJECT,
    ),
    CANCEL: buildPermission(
      PermissionModule.LEAVE_APPLICATION,
      PermissionAction.CANCEL,
    ),
  },

  HOLIDAY: {
    READ: buildPermission(PermissionModule.HOLIDAY, PermissionAction.READ),
    CREATE: buildPermission(PermissionModule.HOLIDAY, PermissionAction.CREATE),
    UPDATE: buildPermission(PermissionModule.HOLIDAY, PermissionAction.UPDATE),
    DELETE: buildPermission(PermissionModule.HOLIDAY, PermissionAction.DELETE),
  },

  LEAVE_POLICY: {
    READ: buildPermission(PermissionModule.LEAVE_POLICY, PermissionAction.READ),
    CREATE: buildPermission(
      PermissionModule.LEAVE_POLICY,
      PermissionAction.CREATE,
    ),
    UPDATE: buildPermission(
      PermissionModule.LEAVE_POLICY,
      PermissionAction.UPDATE,
    ),
    DELETE: buildPermission(
      PermissionModule.LEAVE_POLICY,
      PermissionAction.DELETE,
    ),
  },

  USER: {
    CREATE: buildPermission(PermissionModule.USER, PermissionAction.CREATE),
    READ: buildPermission(PermissionModule.USER, PermissionAction.READ),
    UPDATE: buildPermission(PermissionModule.USER, PermissionAction.UPDATE),
    DELETE: buildPermission(PermissionModule.USER, PermissionAction.DELETE),
  },

  ROLES_AND_PERMISSIONS: {
    CREATE: buildPermission(
      PermissionModule.ROLES_AND_PERMISSIONS,
      PermissionAction.CREATE,
    ),
    READ: buildPermission(
      PermissionModule.ROLES_AND_PERMISSIONS,
      PermissionAction.READ,
    ),
    UPDATE: buildPermission(
      PermissionModule.ROLES_AND_PERMISSIONS,
      PermissionAction.UPDATE,
    ),
    DELETE: buildPermission(
      PermissionModule.ROLES_AND_PERMISSIONS,
      PermissionAction.DELETE,
    ),
  },

  CLIENT: {
    READ: buildPermission(PermissionModule.CLIENT, PermissionAction.READ),
    CREATE: buildPermission(PermissionModule.CLIENT, PermissionAction.CREATE),
    UPDATE: buildPermission(PermissionModule.CLIENT, PermissionAction.UPDATE),
    DELETE: buildPermission(PermissionModule.CLIENT, PermissionAction.DELETE),
  },

  QUOTATION: {
    READ: buildPermission(PermissionModule.QUOTATION, PermissionAction.READ),
    CREATE: buildPermission(
      PermissionModule.QUOTATION,
      PermissionAction.CREATE,
    ),
    UPDATE: buildPermission(
      PermissionModule.QUOTATION,
      PermissionAction.UPDATE,
    ),
    DELETE: buildPermission(
      PermissionModule.QUOTATION,
      PermissionAction.DELETE,
    ),
    SEND: buildPermission(PermissionModule.QUOTATION, PermissionAction.SEND),
    ACCEPT: buildPermission(
      PermissionModule.QUOTATION,
      PermissionAction.ACCEPT,
    ),
    REJECT: buildPermission(
      PermissionModule.QUOTATION,
      PermissionAction.REJECT,
    ),
    CANCEL: buildPermission(
      PermissionModule.QUOTATION,
      PermissionAction.CANCEL,
    ),
  },

  CLIENT_ADVANCE: {
    READ: buildPermission(PermissionModule.CLIENT_ADVANCE, PermissionAction.READ),
    CREATE: buildPermission(PermissionModule.CLIENT_ADVANCE, PermissionAction.CREATE),
    UPDATE: buildPermission(PermissionModule.CLIENT_ADVANCE, PermissionAction.UPDATE),
    DELETE: buildPermission(PermissionModule.CLIENT_ADVANCE, PermissionAction.DELETE),
    CANCEL: buildPermission(PermissionModule.CLIENT_ADVANCE, PermissionAction.CANCEL),
    /** DRAFT → PROCESSED (attach the Payment). */
    RECEIVE: buildPermission(PermissionModule.CLIENT_ADVANCE, PermissionAction.RECEIVE),
    /** PROCESSED → REFUNDED (full remaining balance only). */
    REFUND: buildPermission(PermissionModule.CLIENT_ADVANCE, PermissionAction.REFUND),
  },

  CLIENT_ADVANCE_SETTLEMENT: {
    READ: buildPermission(
      PermissionModule.CLIENT_ADVANCE_SETTLEMENT,
      PermissionAction.READ,
    ),
    CREATE: buildPermission(
      PermissionModule.CLIENT_ADVANCE_SETTLEMENT,
      PermissionAction.CREATE,
    ),
  },

  EMPLOYEE_ADVANCE: {
    READ: buildPermission(PermissionModule.EMPLOYEE_ADVANCE, PermissionAction.READ),
    CREATE: buildPermission(PermissionModule.EMPLOYEE_ADVANCE, PermissionAction.CREATE),
    UPDATE: buildPermission(PermissionModule.EMPLOYEE_ADVANCE, PermissionAction.UPDATE),
    DELETE: buildPermission(PermissionModule.EMPLOYEE_ADVANCE, PermissionAction.DELETE),
    CANCEL: buildPermission(PermissionModule.EMPLOYEE_ADVANCE, PermissionAction.CANCEL),
    /** DRAFT → PROCESSED (attach the Payment). */
    DISBURSE: buildPermission(PermissionModule.EMPLOYEE_ADVANCE, PermissionAction.DISBURSE),
    /**
     * PROCESSED → REFUNDED (full remaining balance only). Not seeded to any role yet —
     * super-admin-only for now, matching the existing CLIENT_ADVANCE.REFUND precedent.
     */
    REFUND: buildPermission(PermissionModule.EMPLOYEE_ADVANCE, PermissionAction.REFUND),
  },

  EMPLOYEE_ADVANCE_SETTLEMENT: {
    READ: buildPermission(
      PermissionModule.EMPLOYEE_ADVANCE_SETTLEMENT,
      PermissionAction.READ,
    ),
    CREATE: buildPermission(
      PermissionModule.EMPLOYEE_ADVANCE_SETTLEMENT,
      PermissionAction.CREATE,
    ),
  },

  PAYROLL: {
    READ: buildPermission(PermissionModule.PAYROLL, PermissionAction.READ),
    CREATE: buildPermission(PermissionModule.PAYROLL, PermissionAction.CREATE),
    UPDATE: buildPermission(PermissionModule.PAYROLL, PermissionAction.UPDATE),
    FINALIZE: buildPermission(
      PermissionModule.PAYROLL,
      PermissionAction.FINALIZE,
    ),
    CANCEL: buildPermission(PermissionModule.PAYROLL, PermissionAction.CANCEL),
    DELETE: buildPermission(PermissionModule.PAYROLL, PermissionAction.DELETE),
    /** Pay out a single payroll item's salary (creates the Payment). */
    DISBURSE: buildPermission(
      PermissionModule.PAYROLL,
      PermissionAction.DISBURSE,
    ),
  },

  MATERIAL_REQUEST: {
    READ: buildPermission(
      PermissionModule.MATERIAL_REQUEST,
      PermissionAction.READ,
    ),
    CREATE: buildPermission(
      PermissionModule.MATERIAL_REQUEST,
      PermissionAction.CREATE,
    ),
    UPDATE: buildPermission(
      PermissionModule.MATERIAL_REQUEST,
      PermissionAction.UPDATE,
    ),
    DELETE: buildPermission(
      PermissionModule.MATERIAL_REQUEST,
      PermissionAction.DELETE,
    ),

    APPROVE: buildPermission(
      PermissionModule.MATERIAL_REQUEST,
      PermissionAction.APPROVE,
    ),
    REJECT: buildPermission(
      PermissionModule.MATERIAL_REQUEST,
      PermissionAction.REJECT,
    ),
    CANCEL: buildPermission(
      PermissionModule.MATERIAL_REQUEST,
      PermissionAction.CANCEL,
    ),
  },

  INVOICE: {
    READ: buildPermission(PermissionModule.INVOICE, PermissionAction.READ),
    CREATE: buildPermission(PermissionModule.INVOICE, PermissionAction.CREATE),
    UPDATE: buildPermission(PermissionModule.INVOICE, PermissionAction.UPDATE),
    DELETE: buildPermission(PermissionModule.INVOICE, PermissionAction.DELETE),
    /** Email invoice PDF to client (`POST .../send` and `.../resend`) */
    SEND: buildPermission(PermissionModule.INVOICE, PermissionAction.SEND),
    /**
     * Void the invoice outright (→ CANCELLED). Refused if any direct payment
     * is applied; if only advance settlements are attached, those are
     * reversed automatically.
     */
    CANCEL: buildPermission(PermissionModule.INVOICE, PermissionAction.CANCEL),
  },

  PAYMENT: {
    READ: buildPermission(PermissionModule.PAYMENT, PermissionAction.READ),
    CREATE: buildPermission(PermissionModule.PAYMENT, PermissionAction.CREATE),
    CANCEL: buildPermission(PermissionModule.PAYMENT, PermissionAction.CANCEL),
  },

  SUPPLIER: {
    READ: buildPermission(PermissionModule.SUPPLIER, PermissionAction.READ),
    CREATE: buildPermission(PermissionModule.SUPPLIER, PermissionAction.CREATE),
    UPDATE: buildPermission(PermissionModule.SUPPLIER, PermissionAction.UPDATE),
    DELETE: buildPermission(PermissionModule.SUPPLIER, PermissionAction.DELETE),
  },

  PURCHASE_ORDER: {
    READ: buildPermission(
      PermissionModule.PURCHASE_ORDER,
      PermissionAction.READ,
    ),
    CREATE: buildPermission(
      PermissionModule.PURCHASE_ORDER,
      PermissionAction.CREATE,
    ),
    UPDATE: buildPermission(
      PermissionModule.PURCHASE_ORDER,
      PermissionAction.UPDATE,
    ),
    APPROVE: buildPermission(
      PermissionModule.PURCHASE_ORDER,
      PermissionAction.APPROVE,
    ),
    CANCEL: buildPermission(
      PermissionModule.PURCHASE_ORDER,
      PermissionAction.CANCEL,
    ),
  },

  EMPLOYMENT_CATEGORY: {
    READ: buildPermission(
      PermissionModule.EMPLOYMENT_CATEGORY,
      PermissionAction.READ,
    ),
    CREATE: buildPermission(
      PermissionModule.EMPLOYMENT_CATEGORY,
      PermissionAction.CREATE,
    ),
    UPDATE: buildPermission(
      PermissionModule.EMPLOYMENT_CATEGORY,
      PermissionAction.UPDATE,
    ),
    DELETE: buildPermission(
      PermissionModule.EMPLOYMENT_CATEGORY,
      PermissionAction.DELETE,
    ),
  },

  DEPARTMENT: {
    CREATE: buildPermission(
      PermissionModule.DEPARTMENT,
      PermissionAction.CREATE,
    ),
    READ: buildPermission(PermissionModule.DEPARTMENT, PermissionAction.READ),
    UPDATE: buildPermission(
      PermissionModule.DEPARTMENT,
      PermissionAction.UPDATE,
    ),
    DELETE: buildPermission(
      PermissionModule.DEPARTMENT,
      PermissionAction.DELETE,
    ),
  },

  DESIGNATION: {
    CREATE: buildPermission(
      PermissionModule.DESIGNATION,
      PermissionAction.CREATE,
    ),
    READ: buildPermission(PermissionModule.DESIGNATION, PermissionAction.READ),
    UPDATE: buildPermission(
      PermissionModule.DESIGNATION,
      PermissionAction.UPDATE,
    ),
    DELETE: buildPermission(
      PermissionModule.DESIGNATION,
      PermissionAction.DELETE,
    ),
  },

  WORK_ASSIGNMENT: {
    READ: buildPermission(
      PermissionModule.WORK_ASSIGNMENT,
      PermissionAction.READ,
    ),
    CREATE: buildPermission(
      PermissionModule.WORK_ASSIGNMENT,
      PermissionAction.CREATE,
    ),
    UPDATE: buildPermission(
      PermissionModule.WORK_ASSIGNMENT,
      PermissionAction.UPDATE,
    ),
  },

  SETTINGS: {
    READ: buildPermission(PermissionModule.SETTINGS, PermissionAction.READ),
    UPDATE: buildPermission(PermissionModule.SETTINGS, PermissionAction.UPDATE),
  },

  PAYMENT_EXPECTATION: {
    READ: buildPermission(
      PermissionModule.PAYMENT_EXPECTATION,
      PermissionAction.READ,
    ),
    CREATE: buildPermission(
      PermissionModule.PAYMENT_EXPECTATION,
      PermissionAction.CREATE,
    ),
    UPDATE: buildPermission(
      PermissionModule.PAYMENT_EXPECTATION,
      PermissionAction.UPDATE,
    ),
    CANCEL: buildPermission(
      PermissionModule.PAYMENT_EXPECTATION,
      PermissionAction.CANCEL,
    ),
  },
  ACTIVITY_LOG: {
    READ: buildPermission(PermissionModule.ACTIVITY_LOG, PermissionAction.READ),
  },

  MATERIAL: {
    READ: buildPermission(PermissionModule.MATERIAL, PermissionAction.READ),
    CREATE: buildPermission(PermissionModule.MATERIAL, PermissionAction.CREATE),
    UPDATE: buildPermission(PermissionModule.MATERIAL, PermissionAction.UPDATE),
    DELETE: buildPermission(PermissionModule.MATERIAL, PermissionAction.DELETE),
  },

  TOOL: {
    READ: buildPermission(PermissionModule.TOOL, PermissionAction.READ),
    CREATE: buildPermission(PermissionModule.TOOL, PermissionAction.CREATE),
    UPDATE: buildPermission(PermissionModule.TOOL, PermissionAction.UPDATE),
    DELETE: buildPermission(PermissionModule.TOOL, PermissionAction.DELETE),
  },

  MATERIAL_IN_OUT: {
    READ: buildPermission(
      PermissionModule.MATERIAL_IN_OUT,
      PermissionAction.READ,
    ),
    CREATE: buildPermission(
      PermissionModule.MATERIAL_IN_OUT,
      PermissionAction.CREATE,
    ),
    UPDATE: buildPermission(
      PermissionModule.MATERIAL_IN_OUT,
      PermissionAction.UPDATE,
    ),
    DELETE: buildPermission(
      PermissionModule.MATERIAL_IN_OUT,
      PermissionAction.DELETE,
    ),
    /** Mark an open row as returned (sets dateIn). */
    RETURN: buildPermission(
      PermissionModule.MATERIAL_IN_OUT,
      PermissionAction.RETURN,
    ),
  },

  TOOL_IN_OUT: {
    READ: buildPermission(PermissionModule.TOOL_IN_OUT, PermissionAction.READ),
    CREATE: buildPermission(
      PermissionModule.TOOL_IN_OUT,
      PermissionAction.CREATE,
    ),
    UPDATE: buildPermission(
      PermissionModule.TOOL_IN_OUT,
      PermissionAction.UPDATE,
    ),
    DELETE: buildPermission(
      PermissionModule.TOOL_IN_OUT,
      PermissionAction.DELETE,
    ),
    /** Mark an open row as returned (sets dateIn). */
    RETURN: buildPermission(
      PermissionModule.TOOL_IN_OUT,
      PermissionAction.RETURN,
    ),
  },

  COMPLAINT_MATERIAL: {
    READ: buildPermission(
      PermissionModule.COMPLAINT_MATERIAL,
      PermissionAction.READ,
    ),
    CREATE: buildPermission(
      PermissionModule.COMPLAINT_MATERIAL,
      PermissionAction.CREATE,
    ),
    UPDATE: buildPermission(
      PermissionModule.COMPLAINT_MATERIAL,
      PermissionAction.UPDATE,
    ),
    DELETE: buildPermission(
      PermissionModule.COMPLAINT_MATERIAL,
      PermissionAction.DELETE,
    ),
    /**
     * The one action outside the date register — an override, rejected if
     * already CANCELLED or already HANDED_BACK_TO_CLIENT.
     */
    CANCEL: buildPermission(
      PermissionModule.COMPLAINT_MATERIAL,
      PermissionAction.CANCEL,
    ),
  },
} as const;

export function getAllRegisteredPermissions(): string[] {
  const list: string[] = [];
  Object.values(Permissions).forEach((actions) => {
    Object.values(actions).forEach((code) => list.push(code));
  });
  return list;
}

export const REGISTERED_PERMISSIONS = getAllRegisteredPermissions();
